import { useState, useEffect } from 'react';
import { CheckCircle, Calendar, FileText, Users, Eye, Award, Activity, Clock, Send, XCircle, AlertTriangle, ChevronDown, ChevronRight, Edit, Circle, Save } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { projectQAService } from '../../services/projectQA';
import { toast } from 'sonner';

interface ComplianceAction {
  id: string;
  description: string;
  owner: string;
  dueDate: string;
  status: 'completed' | 'in-progress' | 'not-started';
  requiresApproval: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approver?: string;
  evidence?: string;
  exemptionRequested?: boolean;
  exemptionStatus?: 'pending' | 'approved' | 'rejected';
  exemptionReason?: string;
}

interface QAStandard {
  id: string;
  name: string;
  category: string;
  applicable: boolean;
  mandatory: boolean;
  complianceScore: number;
  totalActions: number;
  completedActions: number;
  pendingApprovals: number;
  exemptions: number;
  actions: ComplianceAction[];
  description: string;
}

interface ProjectQASectionProps {
  projectId?: number;
}

// Demo QA standards — rendered ONLY when projectId is unset. Real projects
// must never see this seed data; falling through to mock on an outage or a
// fresh project violates feedback_silent_fallback.
export function ProjectQASection({ projectId }: ProjectQASectionProps) {
  const [activeView, setActiveView] = useState<'standards' | 'history' | 'metrics'>('standards');
  const [expandedStandard, setExpandedStandard] = useState<string | null>('STD-001');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showExemptionModal, setShowExemptionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ComplianceAction | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Real projects start empty; demo data is reserved for the !projectId branch.
  // Exposed via `setQaStandards` so future inline editors can mutate state.
  const [qaStandards, setQaStandards] = useState<QAStandard[]>(
    [],
  );

  useEffect(() => {
    if (!projectId) {
      setQaStandards([]);
      return;
    }
    projectQAService
      .get(projectId)
      .then((res) => {
        // Accept whatever the backend returns, even an empty list — never
        // silently fall back to DEMO data for a real project.
        const incoming = Array.isArray(res.qa_data) ? (res.qa_data as QAStandard[]) : [];
        setQaStandards(incoming);
      })
      .catch(() => {
        toast.error('Failed to load QA data');
        setQaStandards([]);
      });
  }, [projectId]);

  const handleSaveStandards = async () => {
    if (!projectId || isSaving) return;
    setIsSaving(true);
    try {
      await projectQAService.update(projectId, qaStandards as unknown as unknown[]);
      setNotification({ message: 'QA standards saved', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    } catch {
      toast.error('Failed to save QA standards');
    } finally {
      setIsSaving(false);
    }
  };

  const overallCompliance = qaStandards.length
    ? Math.round(
        qaStandards.reduce((sum, std) => sum + std.complianceScore, 0) / qaStandards.length,
      )
    : 0;

  const totalActions = qaStandards.reduce((sum, std) => sum + std.totalActions, 0);
  const completedActions = qaStandards.reduce((sum, std) => sum + std.completedActions, 0);
  const pendingApprovals = qaStandards.reduce((sum, std) => sum + std.pendingApprovals, 0);
  const activeExemptions = qaStandards.reduce((sum, std) => sum + std.exemptions, 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'in-progress':
      case 'pending':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'not-started':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const handleRequestExemption = (action: ComplianceAction) => {
    setSelectedAction(action);
    setShowExemptionModal(true);
  };

  const handleSubmitForApproval = (action: ComplianceAction) => {
    setNotification({ message: `${action.description} submitted for approval`, type: 'success' });
    setTimeout(() => setNotification(null), 3000);
  };

  const toggleStandard = (standardId: string) => {
    setExpandedStandard(expandedStandard === standardId ? null : standardId);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-2xl border-2 flex items-center gap-3 ${
          notification.type === 'success' ? 'bg-green-500 border-green-600 text-white' :
          notification.type === 'error' ? 'bg-red-500 border-red-600 text-white' :
          'bg-blue-500 border-blue-600 text-white'
        }`}>
          <span className="font-medium">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="ml-2 hover:bg-white/20 rounded p-1">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <HeroSection
        title="Quality Assurance & Standards Compliance"
        description="Manage QA standards, compliance actions, approvals, and exemptions for the project"
        variant="level4"
        metrics={[
          { label: 'Overall Compliance', value: `${overallCompliance}%`, icon: Award, iconColor: 'bg-green-50' },
          { label: 'Actions Completed', value: `${completedActions}/${totalActions}`, icon: CheckCircle, iconColor: 'bg-blue-50' },
          { label: 'Pending Approvals', value: pendingApprovals.toString(), icon: Clock, iconColor: 'bg-amber-50' },
          { label: 'Active Exemptions', value: activeExemptions.toString(), icon: AlertTriangle, iconColor: 'bg-red-50' },
        ]}
      />

      {/* View Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between px-6">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveView('standards')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeView === 'standards'
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                QA Standards & Actions
              </button>
              <button
                onClick={() => setActiveView('metrics')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeView === 'metrics'
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Compliance Metrics
              </button>
              <button
                onClick={() => setActiveView('history')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeView === 'history'
                    ? 'border-blue-600 text-blue-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Audit History
              </button>
            </div>
            {projectId && (
              <button
                onClick={handleSaveStandards}
                disabled={isSaving}
                className={`mb-2 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                  isSaving
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                title="Save QA standards"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>

        {/* Standards View */}
        {activeView === 'standards' && (
          <div className="p-6 space-y-6">
            {/* Standards List */}
            <div className="space-y-4">
              {qaStandards.map((standard) => (
                <div key={standard.id} className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-all">
                  {/* Standard Header */}
                  <button
                    onClick={() => toggleStandard(standard.id)}
                    className="w-full bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {expandedStandard === standard.id ? (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                          )}
                          <div className="text-left flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-neutral-950">{standard.name}</h3>
                              {standard.mandatory && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded border border-red-300">
                                  MANDATORY
                                </span>
                              )}
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded border border-blue-300">
                                {standard.category}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{standard.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 ml-6">
                          <div className="text-right">
                            <div className="text-sm text-gray-600 mb-1">Compliance</div>
                            <div className={`text-2xl font-bold ${
                              standard.complianceScore >= 90 ? 'text-green-600' :
                              standard.complianceScore >= 75 ? 'text-blue-600' :
                              standard.complianceScore >= 60 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {standard.complianceScore}%
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600 mb-1">Progress</div>
                            <div className="text-lg font-semibold text-neutral-950">
                              {standard.completedActions}/{standard.totalActions}
                            </div>
                          </div>
                          {standard.pendingApprovals > 0 && (
                            <div className="text-right">
                              <div className="text-sm text-gray-600 mb-1">Approvals</div>
                              <div className="text-lg font-semibold text-amber-600">
                                {standard.pendingApprovals}
                              </div>
                            </div>
                          )}
                          {standard.exemptions > 0 && (
                            <div className="text-right">
                              <div className="text-sm text-gray-600 mb-1">Exemptions</div>
                              <div className="text-lg font-semibold text-red-600">
                                {standard.exemptions}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              standard.complianceScore >= 90 ? 'bg-green-500' :
                              standard.complianceScore >= 75 ? 'bg-blue-500' :
                              standard.complianceScore >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${standard.complianceScore}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Actions */}
                  {expandedStandard === standard.id && (
                    <div className="bg-white border-t-2 border-gray-200">
                      <div className="p-6">
                        <h4 className="text-base font-semibold text-neutral-950 mb-4">Compliance Actions Required</h4>
                        <div className="space-y-3">
                          {standard.actions.map((action) => (
                            <div key={action.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all">
                              {/* Action Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-start gap-3 flex-1">
                                  <div className="mt-1">
                                    {action.status === 'completed' ? (
                                      <CheckCircle className="w-5 h-5 text-green-600" />
                                    ) : action.status === 'in-progress' ? (
                                      <Activity className="w-5 h-5 text-blue-600" />
                                    ) : (
                                      <Circle className="w-5 h-5 text-gray-400" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-neutral-950 mb-2">{action.description}</p>
                                    <div className="flex items-center gap-4 text-xs text-gray-600">
                                      <div className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        <span>{action.owner}</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        <span>Due: {action.dueDate}</span>
                                      </div>
                                      {action.evidence && (
                                        <div className="flex items-center gap-1">
                                          <FileText className="w-3 h-3" />
                                          <span>{action.evidence}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <span className={`px-3 py-1 rounded text-xs border font-medium ${getStatusBadge(action.status)}`}>
                                    {action.status === 'not-started' ? 'Not Started' :
                                     action.status === 'in-progress' ? 'In Progress' : 'Completed'}
                                  </span>
                                </div>
                              </div>

                              {/* Approval Status */}
                              {action.requiresApproval && (
                                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="text-xs font-semibold text-amber-700 mb-1">Requires Approval</div>
                                      {action.approver && (
                                        <div className="text-xs text-gray-700">Approver: {action.approver}</div>
                                      )}
                                    </div>
                                    {action.approvalStatus && (
                                      <span className={`px-3 py-1 rounded text-xs border font-medium ${getStatusBadge(action.approvalStatus)}`}>
                                        {action.approvalStatus === 'approved' ? '✓ Approved' :
                                         action.approvalStatus === 'rejected' ? '✗ Rejected' : '⏳ Pending Approval'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Exemption Status */}
                              {action.exemptionRequested && (
                                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-semibold text-red-700">Exemption Requested</div>
                                    {action.exemptionStatus && (
                                      <span className={`px-3 py-1 rounded text-xs border font-medium ${getStatusBadge(action.exemptionStatus)}`}>
                                        {action.exemptionStatus === 'approved' ? '✓ Exemption Approved' :
                                         action.exemptionStatus === 'rejected' ? '✗ Exemption Denied' : '⏳ Pending Review'}
                                      </span>
                                    )}
                                  </div>
                                  {action.exemptionReason && (
                                    <div className="text-xs text-gray-700">
                                      <span className="font-medium">Reason:</span> {action.exemptionReason}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="flex gap-2">
                                {action.evidence && (
                                  <button className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-all text-xs font-medium flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    View Evidence
                                  </button>
                                )}
                                {action.status !== 'completed' && (
                                  <button className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all text-xs font-medium flex items-center gap-1">
                                    <Edit className="w-3 h-3" />
                                    Update Status
                                  </button>
                                )}
                                {action.requiresApproval && action.status === 'completed' && action.approvalStatus === 'pending' && (
                                  <button 
                                    onClick={() => handleSubmitForApproval(action)}
                                    className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-all text-xs font-medium flex items-center gap-1"
                                  >
                                    <Send className="w-3 h-3" />
                                    Submit for Approval
                                  </button>
                                )}
                                {!action.exemptionRequested && action.status !== 'completed' && (
                                  <button 
                                    onClick={() => handleRequestExemption(action)}
                                    className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-all text-xs font-medium flex items-center gap-1 border border-orange-300"
                                  >
                                    <AlertTriangle className="w-3 h-3" />
                                    Request Exemption
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metrics View */}
        {activeView === 'metrics' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {qaStandards.map((standard) => (
                <div key={standard.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-neutral-950 mb-1">{standard.name}</h3>
                    <p className="text-xs text-gray-600">{standard.category}</p>
                  </div>
                  <div className="mb-4">
                    <div className={`text-3xl font-bold ${
                      standard.complianceScore >= 90 ? 'text-green-600' :
                      standard.complianceScore >= 75 ? 'text-blue-600' :
                      standard.complianceScore >= 60 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {standard.complianceScore}%
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Compliance Score</div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div 
                      className={`h-2 rounded-full ${
                        standard.complianceScore >= 90 ? 'bg-green-500' :
                        standard.complianceScore >= 75 ? 'bg-blue-500' :
                        standard.complianceScore >= 60 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${standard.complianceScore}%` }}
                    ></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-gray-600">Actions</div>
                      <div className="font-semibold text-neutral-950">{standard.completedActions}/{standard.totalActions}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Approvals</div>
                      <div className="font-semibold text-amber-600">{standard.pendingApprovals}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History View */}
        {activeView === 'history' && (
          <div className="p-6">
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">Audit history and compliance tracking will appear here</p>
            </div>
          </div>
        )}
      </div>

      {/* Exemption Request Modal */}
      {showExemptionModal && selectedAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-950 mb-4">Request Exemption</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2"><strong>Action:</strong> {selectedAction.description}</p>
              <p className="text-sm text-gray-600 mb-4"><strong>Standard:</strong> {qaStandards.find(s => s.actions.some(a => a.id === selectedAction.id))?.name}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-950 mb-2">Exemption Reason *</label>
              <textarea 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Provide detailed justification for exemption request..."
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-950 mb-2">Alternative Approach (Optional)</label>
              <textarea 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe any alternative measures or compensating controls..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => setShowExemptionModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setNotification({ message: 'Exemption request submitted for review', type: 'success' });
                  setShowExemptionModal(false);
                  setTimeout(() => setNotification(null), 3000);
                }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}